// Hero stage: a laptop that opens, lights up with the real lesson screen,
// pushes in during the date rewind and then steps aside for the three steps.
import * as THREE from '../vendor/three/three.module.min.js';
import { createLaptop, createStudio, loadTexture } from './devices.js';

const OPEN_ANGLE = 1.86;
// centre of the open display: hinge pivot + lid-local centre rotated by the angle
const SCREEN_CENTER = new THREE.Vector3(0, 0.117 + 1.146 * Math.sin(OPEN_ANGLE), -1.071 + 1.146 * Math.cos(OPEN_ANGLE));
const lerp = (a, b, t) => a + (b - a) * t;
const lerpV = (a, b, t) => new THREE.Vector3(lerp(a.x, b.x, t), lerp(a.y, b.y, t), lerp(a.z, b.z, t));

export async function createStage(canvas, { screens }) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  // neutral tone mapping keeps the aluminium and the page's whites clean
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(28, 1, 0.05, 80);
  // neutral silver light: a coloured rim or fill tints the aluminium blue
  await createStudio(renderer, scene, {
    rim: 0xdde3e8,
    rimIntensity: 1.1,
    envIntensity: 0.95,
    keyIntensity: 2.1,
    fillIntensity: 0.9,
    fillColor: 0xeceff2,
  });

  const [textures, lidMark] = await Promise.all([
    Promise.all(screens.map((u) => loadTexture(renderer, u))),
    loadTexture(renderer, 'assets/captures/lid-mark.webp').catch(() => null),
  ]);
  if (lidMark) lidMark.colorSpace = THREE.NoColorSpace;
  // a slightly deeper silver: on a light page the closed lid must not read as white
  const laptop = createLaptop({ screen: textures[0], body: 0xa3a9b1, shadowOpacity: 0.42, lidMark });
  scene.add(laptop.group);
  laptop.setOpen(0);
  // far edge of the closed lid: the highest point on screen in the opening pose
  // (the lid alone: the group also holds the wider contact shadow)
  const lidBox = new THREE.Box3().setFromObject(laptop.lid);
  const lidFarEdge = new THREE.Vector3(0, lidBox.max.y, lidBox.min.z);
  const probe = camera.clone();
  const sticky = canvas.parentElement;
  const copy = sticky.querySelector('[data-hero-copy]');
  let copyBottom = 0;
  // layout position, not getBoundingClientRect: GSAP moves the copy while it enters and scrolls.
  // The last visible line counts (small phones hide the proof row).
  function measureCopy() {
    const last = copy && [...copy.children].reverse().find((el) => el.offsetParent);
    let y = last ? last.offsetHeight : 0;
    for (let n = last; n && n !== sticky; n = n.offsetParent) y += n.offsetTop;
    copyBottom = last ? y - canvas.offsetTop : 0;
  }
  if (copy && 'ResizeObserver' in window) new ResizeObserver(measureCopy).observe(copy);

  // a soft mint halo on the floor, the aurora reflected under the laptop
  const glowTex = (() => {
    const c = document.createElement('canvas');
    c.width = c.height = 256;
    const g = c.getContext('2d');
    const grd = g.createRadialGradient(128, 128, 0, 128, 128, 128);
    grd.addColorStop(0, 'rgba(120,205,160,0.5)');
    grd.addColorStop(0.5, 'rgba(92,198,220,0.18)');
    grd.addColorStop(1, 'rgba(92,198,220,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 256, 256);
    return new THREE.CanvasTexture(c);
  })();
  const floorGlow = new THREE.Mesh(
    new THREE.PlaneGeometry(9, 5),
    new THREE.MeshBasicMaterial({ map: glowTex, transparent: true, depthWrite: false }),
  );
  floorGlow.rotation.x = -Math.PI / 2;
  floorGlow.position.set(0, -0.002, -0.6);
  scene.add(floorGlow);

  let width = 1;
  let height = 1;
  let visible = true;
  let state = { lid: 0, glow: 0, push: 0, steps: 0, screen: 0 };
  let pointer = { x: 0, y: 0, tx: 0, ty: 0 };

  function resize() {
    const r = canvas.getBoundingClientRect();
    width = Math.max(1, r.width);
    height = Math.max(1, r.height);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    measureCopy();
  }
  resize();
  window.addEventListener('resize', resize);
  window.addEventListener('pointermove', (e) => {
    pointer.tx = (e.clientX / window.innerWidth - 0.5) * 2;
    pointer.ty = (e.clientY / window.innerHeight - 0.5) * 2;
  });

  function frame(time) {
    const aspect = width / height;
    const portrait = aspect < 0.9;
    const s = state;
    const angle = OPEN_ANGLE * s.lid;
    laptop.setOpen(angle);

    // screen: brightness and cross-fades between captures
    const mat = laptop.screenMaterial;
    mat.uniforms.uBrightness.value = 0.02 + 0.98 * s.glow;
    const idx = Math.min(1, Math.floor(s.screen));
    mat.uniforms.mapA.value = textures[idx];
    mat.uniforms.mapB.value = textures[idx + 1];
    mat.uniforms.uMix.value = s.screen - idx;

    // laptop placement: centred, then aside for the steps (desktop only)
    const aside = portrait ? 0 : s.steps;
    // far enough right that the steps column (up to 42 % of the width) stays clear
    laptop.group.position.x = lerp(0, 1.85, aside);
    laptop.group.position.y = 0;
    laptop.group.rotation.y = lerp(0, -0.34, aside) + Math.sin(time * 0.00025) * 0.012;

    // camera: fit widths analytically so every aspect ratio frames the same
    camera.fov = portrait ? 40 : 28;
    camera.updateProjectionMatrix();
    const viewW = (d) => 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * aspect * d;
    const distFor = (w, fill) => w / fill / viewW(1);

    // 1. closed laptop low in the frame, headline above
    // the target sits above the lid so the closed laptop rests below the headline
    const closedTgt = new THREE.Vector3(0, portrait ? 2.25 : 1.85, -0.1);
    const closedDir = new THREE.Vector3(0, 0.44, 1).normalize();
    const closedPos = closedTgt.clone().addScaledVector(closedDir, distFor(3.126, portrait ? 0.92 : 0.46));
    // the headline wraps differently at each size: drop the picture until the lid clears the copy
    probe.fov = camera.fov;
    probe.aspect = aspect;
    probe.updateProjectionMatrix();
    probe.position.copy(closedPos);
    probe.lookAt(closedTgt);
    probe.updateMatrixWorld();
    const lidTop = ((1 - lidFarEdge.clone().project(probe).y) / 2) * height;
    // on very short screens this can take the lid below the fold; it rises with the push
    const drop = Math.max(0, copyBottom + 28 - lidTop) * (1 - s.push);

    // 2. square-on to the open display
    const n = new THREE.Vector3(0, Math.sin(OPEN_ANGLE - Math.PI / 2), Math.cos(OPEN_ANGLE - Math.PI / 2));
    const center = SCREEN_CENTER.clone();
    // desktop: display on the right, the date and the quote on the left
    const side = portrait ? 0 : -1.0;
    const pushTgt = center.clone().add(new THREE.Vector3(side, portrait ? -0.25 : -0.05, 0));
    const pushPos = center.clone().add(new THREE.Vector3(side, 0, 0)).addScaledVector(n, distFor(3.025, portrait ? 0.96 : 0.54));

    // 3. laptop aside (desktop) or raised above the steps card (portrait)
    const stepsTgt = portrait ? new THREE.Vector3(0, 0.95, -0.7) : new THREE.Vector3(0.62, 0.78, -0.55);
    const stepsDir = new THREE.Vector3(portrait ? 0 : -0.06, 0.3, 1).normalize();
    const stepsPos = stepsTgt.clone().addScaledVector(stepsDir, distFor(3.126, portrait ? 0.9 : 0.46));

    let pos = lerpV(closedPos, pushPos, s.push);
    let tgt = lerpV(closedTgt, pushTgt, s.push);
    pos = lerpV(pos, stepsPos, s.steps);
    tgt = lerpV(tgt, stepsTgt, s.steps);

    pointer.x += (pointer.tx - pointer.x) * 0.05;
    pointer.y += (pointer.ty - pointer.y) * 0.05;
    const sway = 1 - s.push * (1 - s.steps);
    pos.x += pointer.x * 0.22 * sway;
    pos.y += -pointer.y * 0.12 * sway;

    camera.position.copy(pos);
    camera.lookAt(tgt);
    if (drop > 0.5) camera.setViewOffset(width, height, 0, -drop, width, height);
    else if (camera.view?.enabled) camera.clearViewOffset();
    floorGlow.material.opacity = 0.35 + 0.35 * s.glow;
    renderer.render(scene, camera);
  }

  let raf = 0;
  const loop = (t) => {
    raf = requestAnimationFrame(loop);
    if (visible) frame(t);
  };
  raf = requestAnimationFrame(loop);

  return {
    update(next) {
      state = { ...next };
    },
    observe(el) {
      new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting;
      }).observe(el);
    },
    renderOnce(next, t = 0) {
      state = { ...next };
      frame(t);
    },
    dispose() {
      cancelAnimationFrame(raf);
      renderer.dispose();
    },
  };
}
